import { v4 as uuidv4 } from 'uuid'
import { CONDUIT_LONGPOLL } from './endpoints'
import { dispatch } from './transportEvents'

import type { subscriptionExchange } from '@urql/core/dist/types'
import type { Sink } from 'graphql-ws'

type SubscriptionForwarder = Parameters<typeof subscriptionExchange>[0]['forwardSubscription']
type SubscriptionOperation = Parameters<SubscriptionForwarder>[0]
type SubscriptionIdentifier = Readonly<string>
type ConduitToken = Readonly<string>

/** @see https://github.com/enisdenjo/graphql-ws/blob/master/PROTOCOL.md */
const MESSAGES = {
  CONNECTION_INIT: 'connection_init',
  CONNECTION_ACK: 'connection_ack',
  SUBSCRIBE: 'subscribe',
  COMPLETE: 'complete',
  PING: 'ping',
  PONG: 'pong'
}

export class LongPollClient {
  private token!: ConduitToken
  private live = false
  private abortController = new AbortController()
  private initStatusPromise: Promise<boolean>
  private awaitingAck: Promise<void>
  private onAck!: () => void
  private subscriptions = new Map<SubscriptionIdentifier, Sink>()

  constructor () {
    this.awaitingAck = new Promise(resolve => {
      this.onAck = () => resolve()
    })
    this.initStatusPromise = this.getToken()
      .then(token => { this.token = token })
      .then(() => this.startUpdateLoop())
      .then(() => this.sendConnectionInit())
      .then(() => this.awaitingAck)
      .then(() => true)
  }

  /**
   * Subscribes through the LongPoll following the config parameters. It
   * uses the `sink` to emit received data or errors. Returns a _cleanup_
   * function used for dropping the subscription and cleaning stuff up.
   */
  subscribe (operation: SubscriptionOperation, sink: Sink): () => void {
    const key = `${uuidv4()}-${operation.key}`
    this.initStatusPromise.then((initOK) => {
      if (initOK) {
        this.createSubscription(key, operation, sink)
      } else {
        sink.error(new Error('Cannot init long polling'))
        sink.complete()
      }
    })
    return () => {
      this.longPollPost({
        type: MESSAGES.COMPLETE,
        id: key
      })
      this.subscriptions.get(key)?.complete()
    }
  }

  /**
   * Stop long polling immediately
   */
  abort (): void {
    this.live = false
    this.abortController.abort()
  }

  private startUpdateLoop () {
    if (!this.live) {
      this.live = true
      this.continueUpdateLoop()
    }
  }

  private async continueUpdateLoop () {
    if (!this.live) return

    try {
      const resp = await this.getNextMessage()
      await this.serverMessageHandler(resp)
      this.continueUpdateLoop()
    } catch (error) {
      dispatch('error', { transport: 'longpoll', stage: 'loop', error })
      setTimeout(() => this.continueUpdateLoop(), 2000) // Try again after a moment
    }
  }

  private async serverMessageHandler (response: Response) {
    const data = await response.json()

    data.messages.forEach((message: string) => {
      if (message === MESSAGES.PING) {
        return this.sendPong()
      }
      const data = JSON.parse(message)
      if (data.type === MESSAGES.CONNECTION_ACK) {
        this.onAck()
      } else {
        this.subscriptions.get(data.id)?.next(data.payload)
      }
    })
  }

  private async createSubscription (key: SubscriptionIdentifier, operation: SubscriptionOperation, sink: Sink) {
    try {
      const response = await this.longPollPost({
        type: MESSAGES.SUBSCRIBE,
        id: key,
        payload: {
          query: operation.query,
          variables: operation.variables
        }
      })
      const data = await response.json()
      if (data.status === 200) {
        this.subscriptions.set(key, sink)
      } else {
        throw new Error('Error subscribing to channel')
      }
    } catch (e) {
      sink.error(e)
    }
  }

  private longPollPost (body: Parameters<JSON['stringify']>[0]) {
    return fetch(`${CONDUIT_LONGPOLL}?vsn=2.0.0&token=${this.token}`, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(body)
    })
  }

  private sendPong () {
    // Conduit doesn't seem to handle 'pong's well, and doesn't need them anyway, so ignore
    // return longPollPost({ type: MESSAGES.PONG })
  }

  private async sendConnectionInit () {
    try {
      const response = await this.longPollPost({ type: MESSAGES.CONNECTION_INIT })
      return response.status === 200
    } catch (e) {
      return false
    }
  }

  private async getToken () {
    try {
      const response = await fetch(`${CONDUIT_LONGPOLL}?vsn=2.0.0`, {
        method: 'GET',
        cache: 'no-cache'
      })
      const json = await response.json()
      return json.token
    } catch (e) {}
    return ''
  }

  private getNextMessage () {
    return fetch(`${CONDUIT_LONGPOLL}?vsn=2.0.0&token=${this.token}`, {
      method: 'GET',
      cache: 'no-cache',
      signal: this.abortController.signal
    })
  }
}
